import { useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import { Cloud, HardDrive, Pencil, Plus, RefreshCcw, Trash2 } from 'lucide-react';
import { Badge, Button, Card, ConfirmModal, Field, Input, Modal, Select, Toggle } from '@/components/ui';
import { useSpaces } from '@/lib/spaces';
import { reducers } from '@/lib/stdb';
import { attempt, errorMessage, toast } from '@/lib/toast';
import { saveExportToSource, s3Config, saveSource, sourceWhere, testSource, useStorageSources, verifyLinked, workerConfig, workerRootNames, type SourceForm, type SourceRow } from '@/lib/sources';

const EMPTY: SourceForm = {
  name: '',
  kind: 's3',
  spaceId: '',
  allowWrite: false,
  s3: { endpoint: '', region: '', bucket: '', prefix: '', forcePathStyle: false, accessKeyId: '', secretAccessKey: '' },
  worker: { workerId: '', root: '', path: '' },
};

function formOf(s: SourceRow): SourceForm {
  const c = s3Config(s);
  const w = workerConfig(s);
  return {
    name: s.name,
    kind: s.kind === 'worker' ? 'worker' : 's3',
    spaceId: s.spaceId,
    allowWrite: s.allowWrite,
    s3: c ? { endpoint: c.endpoint, region: c.region, bucket: c.bucket, prefix: c.prefix, forcePathStyle: c.forcePathStyle, accessKeyId: c.accessKeyId, secretAccessKey: '' } : EMPTY.s3,
    worker: w ?? EMPTY.worker,
  };
}

/**
 * Settings → Storage connections: buckets and network folders the team can
 * browse in the asset library. Admins add, change, test and remove them;
 * credentials go to the server and are never shown again.
 */
export function StorageConnections({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const { sources } = useStorageSources(orgId);
  const sp = useSpaces(orgId);
  const [workers] = useTable(tables.orgWorkers);
  const orgWorkers = useMemo(() => workers.filter((w) => w.orgId === orgId), [workers, orgId]);
  const workerName = (id: string) => orgWorkers.find((w) => w.id === id)?.name;
  const [editing, setEditing] = useState<{ id: string | null; form: SourceForm } | null>(null);
  const [deleting, setDeleting] = useState<SourceRow | null>(null);
  const [testing, setTesting] = useState('');

  const test = async (s: SourceRow) => {
    setTesting(s.id);
    try {
      const r = await testSource(s.id);
      if (r.ok) toast.success(`${s.name} works`);
      else toast.error(`${s.name}: ${r.error}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setTesting('');
    }
  };

  /** Looks for linked originals that are gone (or back): they are flagged in the asset library. */
  const check = async (s: SourceRow) => {
    setTesting(`check:${s.id}`);
    try {
      const r = await verifyLinked(s.id);
      if (!r.checked) toast.success(`Nothing is linked from ${s.name}`);
      else if (r.missing) toast.error(`${s.name}: ${r.missing} of ${r.checked} linked file${r.checked === 1 ? '' : 's'} missing`);
      else toast.success(`${s.name}: all ${r.checked} linked file${r.checked === 1 ? '' : 's'} found`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setTesting('');
    }
  };

  return (
    <Card className="p-5" data-storage-connections>
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="font-semibold">Storage connections</h2>
        {canManage ? (
          <Button size="sm" onClick={() => setEditing({ id: null, form: EMPTY })}>
            <Plus size={14} /> Add connection
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-xs text-faint">
        Company storage the team can browse under <b>Asset library → Sources</b> and import from: an S3-compatible bucket (AWS S3, R2, MinIO, Backblaze, Wasabi), or a network
        folder such as a NAS share mounted on one of your workers.
      </p>
      {sources.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-muted">No connections yet.</div>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {sources.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-source-row={s.id}>
              {s.kind === 's3' ? <Cloud size={16} className="text-muted" /> : <HardDrive size={16} className="text-muted" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                  {s.name}
                  <Badge>{s.spaceId ? `Space: ${sp.spaceById.get(s.spaceId)?.name ?? '…'}` : 'Whole organization'}</Badge>
                  {s.allowWrite ? <Badge tone="accent">Exports can be saved</Badge> : <Badge>Read-only</Badge>}
                  {s.lastError ? (
                    <span title={s.lastError}>
                      <Badge tone="danger">Not working</Badge>
                    </span>
                  ) : null}
                </div>
                <div className="truncate text-xs text-faint">{sourceWhere(s, workerName)}</div>
                {s.lastError ? <div className="truncate text-xs text-danger">{s.lastError}</div> : null}
              </div>
              {canManage ? (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" loading={testing === s.id} onClick={() => void test(s)} title="Test the connection">
                    <RefreshCcw size={13} /> Test
                  </Button>
                  <Button size="sm" variant="ghost" loading={testing === `check:${s.id}`} onClick={() => void check(s)} title="Check that the files linked from here are still there" data-source-check>
                    Check links
                  </Button>
                  <Button size="icon-sm" variant="ghost" onClick={() => setEditing({ id: s.id, form: formOf(s) })} aria-label="Edit connection">
                    <Pencil size={14} />
                  </Button>
                  <Button size="icon-sm" variant="ghost" onClick={() => setDeleting(s)} aria-label="Delete connection">
                    <Trash2 size={14} />
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <SourceDialog
          orgId={orgId}
          id={editing.id}
          initial={editing.form}
          hasSecret={!!sources.find((s) => s.id === editing.id)?.hasSecret}
          spaces={sp.spaces.map((s) => ({ id: s.id, name: s.name, restricted: s.restricted }))}
          workers={orgWorkers.map((w) => ({ id: w.id, name: w.name, online: w.online, roots: workerRootNames(w) }))}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete connection"
        danger
        confirmLabel="Delete"
        message={`"${deleting?.name}" disappears from Sources and its credentials are deleted. Files imported from it stay in the library; files linked in place are marked as missing. Nothing in the storage itself is touched.`}
        onConfirm={async () => {
          if (deleting) await attempt(() => reducers().deleteStorageSource({ id: deleting.id }), 'Connection deleted');
          setDeleting(null);
        }}
      />
    </Card>
  );
}

function SourceDialog({
  orgId,
  id,
  initial,
  hasSecret,
  spaces,
  workers,
  onClose,
}: {
  orgId: string;
  id: string | null;
  initial: SourceForm;
  hasSecret: boolean;
  spaces: { id: string; name: string; restricted: boolean }[];
  workers: { id: string; name: string; online: boolean; roots: string[] }[];
  onClose: () => void;
}) {
  const [f, setF] = useState<SourceForm>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (p: Partial<SourceForm>) => setF((x) => ({ ...x, ...p }));
  const setS3 = (p: Partial<SourceForm['s3']>) => setF((x) => ({ ...x, s3: { ...x.s3, ...p } }));
  const setW = (p: Partial<SourceForm['worker']>) => setF((x) => ({ ...x, worker: { ...x.worker, ...p } }));
  const sharing = workers.filter((w) => w.roots.length);
  const worker = workers.find((w) => w.id === f.worker.workerId);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await saveSource(orgId, id, f);
      toast.success(id ? 'Connection saved' : 'Connection added');
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={id ? 'Edit storage connection' : 'Add storage connection'}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()} data-save-source>
            {id ? 'Test and save' : 'Test and add'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Studio NAS" autoFocus data-source-name />
          </Field>
          <Field label="Type">
            <Select className="w-full" value={f.kind} onChange={(e) => set({ kind: e.target.value as SourceForm['kind'] })} disabled={!!id} data-source-kind>
              <option value="s3">S3-compatible bucket</option>
              <option value="worker">Network folder (worker)</option>
            </Select>
          </Field>
        </div>
        <Field label="Who can browse it" hint="Restricted spaces limit it to their members">
          <Select className="w-full" value={f.spaceId} onChange={(e) => set({ spaceId: e.target.value })}>
            <option value="">Everyone in the organization</option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                Space: {s.name}
                {s.restricted ? ' 🔒' : ''}
              </option>
            ))}
          </Select>
        </Field>

        {f.kind === 's3' ? (
          <>
            <Field label="Endpoint" hint="Empty for AWS S3">
              <Input value={f.s3.endpoint} onChange={(e) => setS3({ endpoint: e.target.value })} placeholder="https://<account>.r2.cloudflarestorage.com" data-source-endpoint />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Bucket">
                <Input value={f.s3.bucket} onChange={(e) => setS3({ bucket: e.target.value })} data-source-bucket />
              </Field>
              <Field label="Region">
                <Input value={f.s3.region} onChange={(e) => setS3({ region: e.target.value })} placeholder="auto" />
              </Field>
              <Field label="Folder (prefix)">
                <Input value={f.s3.prefix} onChange={(e) => setS3({ prefix: e.target.value })} placeholder="footage/" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Access key id">
                <Input value={f.s3.accessKeyId} onChange={(e) => setS3({ accessKeyId: e.target.value })} autoComplete="off" data-source-key />
              </Field>
              <Field label="Secret access key" hint={hasSecret ? 'Stored; enter to replace' : undefined}>
                <Input
                  type="password"
                  value={f.s3.secretAccessKey}
                  onChange={(e) => setS3({ secretAccessKey: e.target.value })}
                  autoComplete="new-password"
                  placeholder={hasSecret ? '••••••••' : ''}
                  data-source-secret
                />
              </Field>
            </div>
            <div className="flex flex-wrap gap-5">
              <Toggle checked={f.s3.forcePathStyle} onChange={(v) => setS3({ forcePathStyle: v })} label="Path-style URLs (MinIO)" />
              <Toggle checked={f.allowWrite} onChange={(v) => set({ allowWrite: v })} label="Allow saving exports into it" />
            </div>
            <p className="text-xs text-faint">
              Read-only credentials are enough unless exports are saved into it. The secret is encrypted on the server and never shown again. Files are copied by vidmakr's
              workers, not through your browser.
            </p>
          </>
        ) : (
          <>
            {sharing.length === 0 ? (
              <div className="rounded-lg border border-warn/40 bg-warn/10 p-3 text-xs text-muted">
                None of your workers shares a folder yet. On a machine that has the NAS mounted, run{' '}
                <code className="rounded bg-panel-3 px-1">vidmakr-worker share NAS /mnt/nas</code> (or start the worker with{' '}
                <code className="rounded bg-panel-3 px-1">SOURCE_ROOTS=NAS=/mnt/nas</code>) and restart it.
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Worker">
                <Select className="w-full" value={f.worker.workerId} onChange={(e) => setW({ workerId: e.target.value, root: '' })} data-source-worker>
                  <option value="">Choose…</option>
                  {sharing.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                      {w.online ? '' : ' (offline)'}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Shared folder">
                <Select className="w-full" value={f.worker.root} onChange={(e) => setW({ root: e.target.value })} data-source-root>
                  <option value="">Choose…</option>
                  {(worker?.roots ?? []).map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Start in subfolder" hint="Optional">
              <Input value={f.worker.path} onChange={(e) => setW({ path: e.target.value })} placeholder="Projects/2024" />
            </Field>
            <p className="text-xs text-faint">The worker lists the folder and uploads the files you import over its own connection; it must be online to browse.</p>
          </>
        )}
        {error ? (
          <div className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger" data-source-error>
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/** Saves a finished export into a connection that allows it (copied server side). */
export function SaveToSourceModal({ job, onClose }: { job: { id: string; orgId: string; title: string }; onClose: () => void }) {
  const { sources } = useStorageSources(job.orgId);
  const writable = sources.filter((s) => s.allowWrite);
  const [sourceId, setSourceId] = useState('');
  const [folder, setFolder] = useState('Exports');
  const [busy, setBusy] = useState(false);
  const target = sourceId || writable[0]?.id || '';
  const save = async () => {
    setBusy(true);
    try {
      const r = await saveExportToSource(target, job.id, folder);
      toast.success(`Saved as ${r.path}`);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Save to company storage"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!target} loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      {writable.length === 0 ? (
        <p className="text-sm text-muted">No storage connection allows saving exports. An admin can turn it on for a bucket under Settings → Storage connections.</p>
      ) : (
        <div className="grid gap-3">
          <Field label="Connection">
            <Select className="w-full" value={target} onChange={(e) => setSourceId(e.target.value)}>
              {writable.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Folder" hint="Inside the connection">
            <Input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Exports" />
          </Field>
          <p className="text-xs text-faint">“{job.title}” is copied by the server; it does not pass through your browser.</p>
        </div>
      )}
    </Modal>
  );
}
